import React from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import BrandLogo from '../brand/BrandLogo';
import { PRODUCT_NAME } from '../../utils/brandCopy';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import type { ThemeModePreference } from '../../theme/tokens';
import { focusRing, scrollToId } from './landingStyles';
import ColorwayPicker from './ColorwayPicker';

const brandLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mr: 'auto',
  textDecoration: 'none',
  color: 'text.primary',
  ...focusRing,
};

const BrandLockup: React.FC = () => (
  <>
    <BrandLogo variant="mark" height={40} />
    <Box
      component="span"
        sx={{
        fontWeight: 800,
        fontSize: { xs: '1.05rem', sm: '1.2rem' },
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {PRODUCT_NAME}
    </Box>
  </>
);

const skipToMain = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  const main = document.getElementById('main');
  if (!main) return;
  main.tabIndex = -1;
  main.focus();
};

const PublicNavbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { preference, setPreference } = useThemeMode();
  const onLanding = location.pathname === '/login' || location.pathname === '/';
  const [menuOpen, setMenuOpen] = React.useState(false);

  const goToSection = (id: string) => {
    setMenuOpen(false);
    if (!onLanding) {
      navigate('/login');
      window.setTimeout(() => scrollToId(id), 0);
      return;
    }
    scrollToId(id);
  };

  return (
    <AppBar
      component="header"
      position="sticky"
      elevation={0}
      sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box
        component="a"
        href="#main"
        onClick={skipToMain}
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: 2000,
          transform: 'translateY(-150%)',
          bgcolor: 'brand.yellow',
          color: 'brand.navy',
          fontWeight: 800,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        Skip to content
      </Box>
      <Toolbar sx={{ gap: 1, flexWrap: 'wrap', minHeight: { xs: 64, md: 72 }, py: 1, alignItems: 'center' }}>
        {onLanding ? (
          <Box
            component="a"
            href="#sign-in"
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              scrollToId('sign-in');
            }}
            sx={brandLinkSx}
          >
            <BrandLockup />
          </Box>
        ) : (
          <Box component={RouterLink} to="/login" sx={brandLinkSx}>
            <BrandLockup />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            width: { xs: '100%', md: 'auto' },
            ml: { md: 0 },
          }}
        >
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
          <Button component={RouterLink} to="/how-it-works" color="inherit" sx={{ minHeight: 44, ...focusRing }}>
            How it works
          </Button>
          <Button component={RouterLink} to="/pricing" color="inherit" sx={{ minHeight: 44, ...focusRing }}>
            Pricing
          </Button>
          <Button component={RouterLink} to="/learn" color="inherit" sx={{ minHeight: 44, ...focusRing }}>
            Learn
          </Button>
          <Button component={RouterLink} to="/neighborhood" color="inherit" sx={{ minHeight: 44, ...focusRing }}>
            Neighborhood
          </Button>
        </Box>

        <Tooltip title="Theme">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preference}
            onChange={(_, value: ThemeModePreference | null) => {
              if (value) setPreference(value);
            }}
            aria-label="Color theme"
            sx={{ '& .MuiToggleButton-root': { px: 1, minWidth: 44, minHeight: 44 } }}
          >
            <ToggleButton value="light" aria-label="Light" sx={focusRing}>
              <LightModeIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="dark" aria-label="Dark" sx={focusRing}>
              <DarkModeIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="system" aria-label="System" sx={focusRing}>
              <SettingsBrightnessIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <ColorwayPicker />
        </Box>

        {onLanding ? (
          <Button
            color="inherit"
            onClick={() => scrollToId('sign-in')}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, ...focusRing }}
          >
            Sign in
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to="/login"
            color="inherit"
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, ...focusRing }}
          >
            Sign in
          </Button>
        )}
        <Button
          component={RouterLink}
          to="/signup"
          variant="contained"
          color="primary"
          sx={{ px: { xs: 1.25, sm: 2 }, whiteSpace: 'nowrap', ...focusRing }}
        >
          Start free
        </Button>
        <IconButton
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls="public-menu"
          onClick={() => setMenuOpen(true)}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, ...focusRing }}
        >
          <MenuIcon />
        </IconButton>
        </Box>
      </Toolbar>
      <Drawer
        id="public-menu"
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 'min(100vw - 48px, 320px)', pt: 1 }} role="navigation" aria-label="Mobile">
          <Typography sx={{ px: 2, py: 1, fontWeight: 800 }}>Menu</Typography>
          <List>
            <ListItemButton component={RouterLink} to="/how-it-works" onClick={() => setMenuOpen(false)} sx={{ minHeight: 48 }}>
              How it works
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/tools" onClick={() => setMenuOpen(false)} sx={{ minHeight: 48 }}>
              Tools
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/pricing" onClick={() => setMenuOpen(false)} sx={{ minHeight: 48 }}>
              Pricing
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/learn" onClick={() => setMenuOpen(false)} sx={{ minHeight: 48 }}>
              Learn
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/neighborhood" onClick={() => setMenuOpen(false)} sx={{ minHeight: 48 }}>
              Neighborhood
            </ListItemButton>
            <Box sx={{ px: 2, py: 1 }}>
              <ColorwayPicker />
            </Box>
            <ListItemButton onClick={() => goToSection('sign-in')}>Sign in</ListItemButton>
            <ListItemButton component={RouterLink} to="/signup" onClick={() => setMenuOpen(false)}>
              Start free
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/disclaimer" onClick={() => setMenuOpen(false)}>
              Disclaimer
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/contact" onClick={() => setMenuOpen(false)}>
              Contact
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default PublicNavbar;
